// src/app-data.ts — the /data feed-status page. Pure rendering off /api/snapshot:
// every feed with its value, observation date, source, provenance flag and
// freshness window. The snapshot itself is the public download.

import { coldStartSnapshot, type SnapshotPayload } from "./snapshot.ts";
import { coldStartBaseline } from "./econ/baseline.ts";
import { FRESHNESS_DAYS, LABOR_SERIES, type Cadence } from "./ingest/labor.ts";
import type { EconBaseline, Field } from "./econ/types.ts";
import type { Source } from "./model/entities.ts";

function provBadge(flag: Source): string {
  const label = flag === "ESTIMATE" ? "EST" : flag;
  return '<span class="prov prov-' + flag + '">' + label + "</span>";
}
const win = (c: Cadence): string => FRESHNESS_DAYS[c] + " d";

interface Row { feed: string; value: string; asof: string; source: string; flag: Source; window: string; }

function tbl(rows: Row[]): string {
  let h = "<thead><tr><th>Feed</th><th>Latest value</th><th>As of</th><th>Source</th><th>Flag</th><th>Window</th></tr></thead><tbody>";
  for (const r of rows) {
    h += "<tr><td>" + r.feed + "</td><td class='num'>" + r.value + "</td><td>" + r.asof + "</td><td>" + r.source + "</td><td>" + provBadge(r.flag) + "</td><td>" + r.window + "</td></tr>";
  }
  return h + "</tbody>";
}

function capexRows(snap: SnapshotPayload): Row[] {
  const p = snap.provenance;
  const d = snap.drivers;
  const y = (name: string, series: string, val: number, f: { source: Source; asOf?: string }): Row => ({
    feed: name, value: val.toFixed(2) + "%", asof: f.asOf ?? "—", source: "FRED:" + series, flag: f.source, window: win("daily"),
  });
  return [
    y("UST 10-year", "DGS10", d.y10, p.yields.y10),
    y("UST 20-year", "DGS20", d.y20, p.yields.y20),
    y("UST 30-year", "DGS30", d.y30, p.yields.y30),
    { feed: "Credit spread multiple", value: d.sm.toFixed(2) + "×", asof: p.spreadMultiple.asOf ?? "—", source: "FRED:ICE BofA OAS", flag: p.spreadMultiple.source, window: win("daily") },
  ];
}

function econRows(econ: EconBaseline): Row[] {
  const cadence: Record<string, Cadence> = {};
  for (const s of LABOR_SERIES) cadence[s.key] = s.cadence;
  const fmt = (k: keyof EconBaseline, f: Field): string => {
    if (k === "unemployment" || k === "labor_share" || k === "knowledge_worker_share") return (f.value * 100).toFixed(1) + "%";
    if (k === "gdp") return "$" + (f.value / 1000).toFixed(1) + "T";
    return (f.value / 1000).toFixed(2) + "M";
  };
  const names: Record<keyof EconBaseline, string> = {
    gdp: "Real GDP", unemployment: "Unemployment", labor_share: "Labor share", employment: "Employment",
    knowledge_worker_share: "Knowledge-worker share", openings: "Job openings", hires: "Hires", separations: "Separations",
  };
  const rows: Row[] = [];
  for (const k of Object.keys(names) as Array<keyof EconBaseline>) {
    const f = econ[k];
    const cad = cadence[k];
    rows.push({ feed: names[k], value: fmt(k, f), asof: f.as_of, source: f.source, flag: f.flag, window: cad ? win(cad) : "annual" });
  }
  return rows;
}

async function boot(): Promise<void> {
  let snap: SnapshotPayload;
  try {
    const res = await fetch("/api/snapshot");
    if (!res.ok) throw new Error("http " + res.status);
    snap = (await res.json()) as SnapshotPayload;
  } catch {
    snap = coldStartSnapshot(new Date().toISOString());
    snap.stale = true;
  }
  const econ = snap.econ ?? coldStartBaseline();

  document.getElementById("asof")!.textContent =
    "Snapshot as of " + snap.asOf.slice(0, 10) + " · generated " + snap.generatedAt.slice(0, 19).replace("T", " ") + "Z";

  const banner = document.getElementById("banner")!;
  if (snap.stale) {
    banner.className = "banner on";
    banner.textContent = "⚠ Some feeds are STALE — a refresh failed; showing last-known values.";
  }

  document.getElementById("tbl_capex")!.innerHTML = tbl(capexRows(snap));
  document.getElementById("tbl_econ")!.innerHTML = tbl(econRows(econ));

  const errs = snap.meta?.errors ?? [];
  if (errs.length) {
    document.getElementById("errnote")!.innerHTML =
      "<b>Last run had " + errs.length + " feed error(s):</b> " +
      errs.map((e) => e.series + " (" + e.error + ")").join("; ") + ".";
  }
}

boot();
